import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { AlertCircle, X } from "lucide-react";

import PlatformAppBar from "../../../components/layout/PlatformAppBar";
import PlatformFooter from "../../../components/layout/PlatformFooter";
import OssPlatformSidebar from "../../../components/layout/oss/OssPlatformSidebar";

import "./OssLayout.css";
import "../account/OssAccountPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const DASHBOARD_REFRESH_INTERVAL_MS = 20 * 60 * 1000;

export default function OssLayout() {
  const { t } = useTranslation("oss-account");
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const navigate = useNavigate();

  const [me, setMe] = React.useState(null);
  const [isLoadingMe, setIsLoadingMe] = React.useState(true);
  const [meError, setMeError] = React.useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const [accountDirty, setAccountDirty] = React.useState(false);
  const [pendingPath, setPendingPath] = React.useState("");
  const [leaveAccountModalOpen, setLeaveAccountModalOpen] =
    React.useState(false);

  const lastLoadedAtRef = React.useRef(0);

  /* =========================================================
     NAVIGATION GUARD
     ========================================================= */

  function requestNavigation(path) {
    const currentPath = window.location.pathname;

    if (currentPath === path) return;

    if (currentPath === "/oss/account" && accountDirty) {
      setPendingPath(path);
      setLeaveAccountModalOpen(true);
      return;
    }

    navigate(path);
  }

  /* =========================================================
     LOAD OSS DATA
     ========================================================= */

  const loadMe = React.useCallback(
    async ({ silent = false } = {}) => {
      if (!isAuthenticated) {
        setMe(null);
        setIsLoadingMe(false);
        return;
      }

      try {
        if (!silent) {
          setIsLoadingMe(true);
        }

        setMeError("");

        const token = await getAccessTokenSilently();

        const res = await fetch(`${API_BASE}/api/oss/me/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          console.error("Failed to load /api/oss/me/", data);

          setMeError(t("layout.loadError"));
          setMe(null);
          return;
        }

        setMe(data);
        lastLoadedAtRef.current = Date.now();
      } catch (error) {
        console.error("Failed to load /api/oss/me/", error);

        setMeError(t("layout.loadError"));
        setMe(null);
      } finally {
        if (!silent) {
          setIsLoadingMe(false);
        }
      }
    },
    [isAuthenticated, getAccessTokenSilently, t],
  );

  React.useEffect(() => {
    loadMe();
  }, [loadMe]);

  /* =========================================================
     PERIODIC DASHBOARD REFRESH
     ========================================================= */

  React.useEffect(() => {
    if (!isAuthenticated) return undefined;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadMe({ silent: true });
      }
    }, DASHBOARD_REFRESH_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      const now = Date.now();
      const msSinceLastLoad = now - lastLoadedAtRef.current;

      if (msSinceLastLoad >= DASHBOARD_REFRESH_INTERVAL_MS) {
        loadMe({ silent: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);

      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, loadMe]);

  /* =========================================================
     ACCOUNT DATA
     ========================================================= */

  const account = me?.account || {};
  const profile = me?.profile || {};
  const organisation = me?.organisation || {};
  const dashboardSummary = me?.dashboard_summary ?? null;

  const fullName =
    account.full_name ||
    `${account.first_name || ""} ${account.last_name || ""}`.trim() ||
    t("layout.defaultUser");

  const initials =
    account.initials ||
    `${account.first_name?.[0] || ""}${account.last_name?.[0] || ""}`.toUpperCase() ||
    t("layout.defaultInitials");

  /* =========================================================
     LEAVE ACCOUNT MODAL
     ========================================================= */

  function cancelLeaveAccount() {
    setLeaveAccountModalOpen(false);
    setPendingPath("");
  }

  function confirmLeaveAccount() {
    sessionStorage.removeItem("legup_oss_account_form_draft");
    sessionStorage.removeItem("legup_oss_account_services_draft");

    setAccountDirty(false);
    setLeaveAccountModalOpen(false);

    if (pendingPath) {
      navigate(pendingPath);
    }

    setPendingPath("");
  }

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div className="oss-dashboard-page">
      <PlatformAppBar
        actorType={account?.primary_actor}
        fullName={fullName}
        initials={initials}
        notificationCount={3}
        homePath="/"
        onOpenMobileMenu={() => setMobileMenuOpen(true)}
        avatarImageUrl={account.image_url || organisation.logo_url || ""}
        avatarFit="contain"
      />

      <div className="oss-dashboard-main">
        <OssPlatformSidebar
          mobileOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          dashboardSummary={dashboardSummary}
          onNavigateRequest={requestNavigation}
        />

        <div className="oss-dashboard-content-shell">
          {!me && isLoadingMe ? (
            <div className="oss-layout-loading">
              <div className="oss-layout-loading__card">
                {t("layout.loading")}
              </div>
            </div>
          ) : meError && !me ? (
            <div className="oss-layout-loading">
              <div className="oss-layout-loading__card oss-layout-loading__card--error">
                {meError}
              </div>
            </div>
          ) : (
            <Outlet
              context={{
                me,
                account,
                profile,
                organisation,
                dashboardSummary,
                refreshMe: loadMe,
                isLoadingMe,
                setAccountDirty,
              }}
            />
          )}
        </div>
      </div>

      {leaveAccountModalOpen ? (
        <div
          className="oss-account-confirm-overlay"
          onClick={cancelLeaveAccount}
        >
          <div
            className="oss-account-confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="oss-account-confirm__icon">
              <AlertCircle size={22} />
            </div>

            <div className="oss-account-confirm__content">
              <h3>{t("confirm.leaveTitle")}</h3>

              <p>{t("confirm.leaveText")}</p>

              <div className="oss-account-confirm__actions">
                <button
                  type="button"
                  className="oss-account-confirm__cancel"
                  onClick={cancelLeaveAccount}
                >
                  {t("confirm.stay")}
                </button>

                <button
                  type="button"
                  className="oss-account-confirm__confirm"
                  onClick={confirmLeaveAccount}
                >
                  {t("confirm.leave")}
                </button>
              </div>
            </div>

            <button
              type="button"
              className="oss-account-confirm__close"
              onClick={cancelLeaveAccount}
              aria-label={t("common.close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}

      <PlatformFooter />
    </div>
  );
}
